import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Video,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock3,
  Eye,
  FileText,
  Globe,
  GripVertical,
  ImageOff,
  Images,
  Layers,
  Mail,
  MapPin,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  Search,
  Trash2,
  UserRound,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { api, type Site } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { ask } from '../components/Confirm';
import { cleanValues, EntityForm, type FieldSpec, useAssetUrls } from '../components/EntityForm';
import { Menu } from '../components/Menu';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Field, Loading, PageHeader, Toggle } from '../components/ui';

function StatCard({ icon, tone, value, label, sub }: { icon: ReactNode; tone: string; value: number; label: string; sub: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-sub">{sub}</span>
      </span>
    </div>
  );
}

export function HotelInfoPage() {
  const site = useSite();
  return <NeedsSite>{site ? <HotelInfo key={site.id} site={site} /> : <PageHeader title="Hotel Information" />}</NeedsSite>;
}

const CONTACT_KEYS = ['phone', 'email', 'address', 'receptionHours', 'website'] as const;

function HotelInfo({ site }: { site: Site }) {
  const { can } = useSession();
  const url = useAssetUrls();
  const qc = useQueryClient();
  const key = ['hotel-info', site.id];
  const info = useQuery({ queryKey: key, queryFn: () => api.get<{ info: Record<string, unknown>; topics: Topic[] }>(`/sites/${site.id}/info`) });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const [order, setOrder] = useState<Topic[] | null>(null);
  const topics = order ?? info.data?.topics ?? [];
  const hero = info.data?.info ?? {};
  const [editingInfo, setEditingInfo] = useState(false);
  const [edit, setEdit] = useState<Topic | 'new' | null>(null);
  const [q, setQ] = useState('');
  const [vis, setVis] = useState('all');
  const [selId, setSelId] = useState<string | null>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const editable = can('ADMIN');

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.put(`/sites/${site.id}/info-topics/order`, { ids }),
    onSettled: async () => {
      await invalidate();
      setOrder(null);
    },
  });
  const patch = useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<Topic> }) => api.patch(`/info-topics/${id}`, body), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/info-topics/${id}`), onSuccess: invalidate });
  const moveTo = (from: number, to: number) => {
    if (to < 0 || to >= topics.length || from === to) return;
    const next = [...topics];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m!);
    reorder.mutate(next.map((x) => x.id));
    setOrder(next);
  };

  const filtering = q.trim() !== '' || vis !== 'all';
  const shown = topics.filter((t) => {
    const s = q.trim().toLowerCase();
    if (s && ![t.title, t.subtitle].some((x) => x?.toLowerCase().includes(s))) return false;
    if (vis === 'visible' && !t.enabled) return false;
    if (vis === 'hidden' && t.enabled) return false;
    return true;
  });
  const sel = topics.find((t) => t.id === selId) ?? topics[0] ?? null;
  const gallery = topics.reduce((n, t) => n + (t.galleryAssetIds?.length ?? 0), 0);
  const heroImg = url(hero.heroImageAssetId);
  const str = (k: string) => (hero[k] as string | null | undefined) || null;

  if (info.isLoading) return <Loading />;
  return (
    <>
      <PageHeader title="Hotel Information" sub="About the hotel, location, rooms, services, gallery and contact details." />
      <div className="stat-strip">
        <StatCard icon={<FileText size={22} />} tone="violet" value={topics.length} label="Topics" sub="Information pages" />
        <StatCard icon={<CheckCircle2 size={22} />} tone="green" value={topics.filter((t) => t.enabled).length} label="Published" sub="Currently visible on TV" />
        <StatCard icon={<UserRound size={22} />} tone="blue" value={CONTACT_KEYS.filter((k) => str(k)).length} label="Contact details" sub="Phone, email, address etc." />
        <StatCard icon={<Images size={22} />} tone="amber" value={gallery} label="Gallery photos" sub="Carousel photos in topics" />
      </div>

      <div className="info-layout">
        <div className="stack">
          <div className="card">
            <div className="pkg-head">
              <span className="pkg-head-icon">
                <Building2 size={22} />
              </span>
              <span style={{ flex: 1 }}>
                <span className="pkg-head-title">Welcome & contact</span>
                <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                  Shown at the top of the Hotel Information page on the TV.
                </span>
              </span>
              {editable && (
                <button className="btn" onClick={() => setEditingInfo(true)}>
                  <Pencil size={16} /> Edit
                </button>
              )}
            </div>
            <div className="info-welcome">
              <div className="info-hero">{heroImg ? <img src={heroImg} alt="" /> : <ImageOff size={28} />}</div>
              <div style={{ minWidth: 0 }}>
                <h3 style={{ margin: 0, fontSize: 18 }}>{str('heroTitle') ?? `Welcome to ${site.name}`}</h3>
                <p className="muted" style={{ margin: '8px 0 0', fontSize: 13.5, lineHeight: 1.5 }}>
                  {str('heroText') ?? 'No intro text yet.'}
                </p>
                <div className="info-contacts">
                  {(
                    [
                      [<Phone size={17} />, 'Phone', 'phone'],
                      [<MapPin size={17} />, 'Address', 'address'],
                      [<Mail size={17} />, 'Email', 'email'],
                      [<Clock3 size={17} />, 'Reception', 'receptionHours'],
                      [<Globe size={17} />, 'Website', 'website'],
                      [<Video size={17} />, 'Hotel video', 'videoUrl'],
                    ] as const
                  ).map(([icon, label, k]) => (
                    <div key={k} className="info-contact">
                      <span className="info-contact-icon">{icon}</span>
                      <span style={{ minWidth: 0 }}>
                        <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                          {label}
                        </span>
                        <span className="info-contact-val">{k === 'videoUrl' ? (str(k) ? 'Set' : '—') : str(k) ?? '—'}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="pkg-head">
              <span className="pkg-head-icon">
                <FileText size={22} />
              </span>
              <span style={{ flex: 1 }}>
                <span className="pkg-head-title">Topics</span>
                <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                  The information pages shown on the TV. Drag to reorder, switch visibility or edit the content.
                </span>
              </span>
              {editable && (
                <button className="btn primary" onClick={() => setEdit('new')}>
                  <Plus size={16} /> Add topic
                </button>
              )}
            </div>
            <div className="filters" style={{ marginTop: 14 }}>
              <label className="search">
                <Search size={16} />
                <input placeholder="Search topics…" value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
              <Select value={vis} onChange={(e) => setVis(e.target.value)} aria-label="Visibility">
                <option value="all">All topics</option>
                <option value="visible">Visible on TV</option>
                <option value="hidden">Hidden</option>
              </Select>
            </div>
            {shown.length === 0 ? (
              <Empty>{topics.length ? 'No topic matches.' : 'No topics yet. Add About Us, Location, Rooms, Services or a Gallery.'}</Empty>
            ) : (
              <div className="table-wrap">
                <table className="fac-table">
                  <thead>
                    <tr>
                      <th style={{ width: 56 }}>#</th>
                      <th>Topic</th>
                      <th>Description</th>
                      <th>Visible</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((t) => {
                      const i = topics.indexOf(t);
                      const img = url(t.imageAssetId);
                      return (
                        <tr
                          key={t.id}
                          className={`${sel?.id === t.id ? 'selected' : ''}${drag === i ? ' dragging' : ''}`}
                          style={!t.enabled ? { opacity: 0.55 } : undefined}
                          onClick={() => setSelId(t.id)}
                          draggable={editable && !filtering}
                          onDragStart={(e) => {
                            setDrag(i);
                            setOrder(topics);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragOver={(e) => {
                            if (drag === null || drag === i) return;
                            e.preventDefault();
                            const next = [...topics];
                            const [m] = next.splice(drag, 1);
                            next.splice(i, 0, m!);
                            setOrder(next);
                            setDrag(i);
                          }}
                          onDragEnd={() => {
                            setDrag(null);
                            if (order) reorder.mutate(order.map((x) => x.id));
                          }}
                        >
                          <td>
                            <span className="row" style={{ gap: 6, flexWrap: 'nowrap', color: 'var(--muted)' }} title={filtering ? 'Clear the filters to reorder' : 'Drag to reorder'}>
                              <GripVertical size={14} className="drag-handle" /> {i + 1}
                            </span>
                          </td>
                          <td>
                            <span className="row" style={{ gap: 12, flexWrap: 'nowrap' }}>
                              <span className="fac-thumb topic">{img ? <img src={img} alt="" /> : <ImageOff size={16} />}</span>
                              <strong>{t.title}</strong>
                            </span>
                          </td>
                          <td className="muted">{t.subtitle || '—'}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <Toggle on={t.enabled} onChange={(on) => editable && patch.mutate({ id: t.id, body: { enabled: on } })} label="Shown on TV" />
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                            {editable && (
                              <>
                                <button className="icon-btn" title="Edit" onClick={() => setEdit(t)}>
                                  <Pencil size={16} />
                                </button>
                                <button className="icon-btn danger-icon" title="Delete" onClick={() => ask(`Delete ${t.title}? It disappears from the TVs.`, () => remove.mutate(t.id))}>
                                  <Trash2 size={16} />
                                </button>
                                <Menu
                                  minWidth={180}
                                  button={({ onClick }) => (
                                    <button type="button" className="icon-btn" title="More" onClick={onClick}>
                                      <MoreHorizontal size={16} />
                                    </button>
                                  )}
                                >
                                  <button type="button" className="more-item" disabled={i === 0} onClick={() => moveTo(i, 0)}>
                                    <ChevronUp size={15} /> Move to top
                                  </button>
                                  <button type="button" className="more-item" disabled={i === topics.length - 1} onClick={() => moveTo(i, topics.length - 1)}>
                                    <ChevronDown size={15} /> Move to bottom
                                  </button>
                                </Menu>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <ErrorBox error={patch.error ?? remove.error ?? reorder.error} />
          </div>
        </div>

        <TopicPreview site={site} topics={topics} sel={sel} onSelect={setSelId} url={url} onEdit={editable && sel ? () => setEdit(sel) : undefined} />
      </div>

      {editingInfo && (
        <EntityForm
          title="Welcome & contact"
          fields={infoFields}
          wide
          initial={{ ...hero, heroImageUrl: heroImg }}
          onClose={() => setEditingInfo(false)}
          onSave={async (v) => {
            await api.put(`/sites/${site.id}/info`, cleanValues(v, infoFields));
            await invalidate();
          }}
        />
      )}
      {edit && (
        <EntityForm
          title={edit === 'new' ? 'Add topic' : `Edit ${edit.title}`}
          fields={topicFields}
          wide
          initial={edit === 'new' ? { icon: 'info', enabled: true } : { ...edit, imageUrl: url(edit.imageAssetId) }}
          onClose={() => setEdit(null)}
          onSave={async (v) => {
            const body = cleanValues(v, topicFields);
            if (edit === 'new') await api.post(`/sites/${site.id}/info-topics`, body);
            else await api.patch(`/info-topics/${edit.id}`, body);
            await invalidate();
          }}
        />
      )}
    </>
  );
}

function TopicPreview({
  site,
  topics,
  sel,
  onSelect,
  url,
  onEdit,
}: {
  site: Site;
  topics: Topic[];
  sel: Topic | null;
  onSelect: (id: string) => void;
  url: (id: unknown) => string | null;
  onEdit?: () => void;
}) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  const b = site.branding as Record<string, unknown>;
  const logo = url(b.hotelLogoAssetId);
  const accent = (b.accentColor as string) || '#6E4BFF';
  const img = sel ? url(sel.imageAssetId) : null;
  const visible = topics.filter((t) => t.enabled);
  const opts = { timeZone: site.timezone } as const;
  return (
    <div className="card info-preview">
      <div className="pkg-head">
        <span className="pkg-head-icon">
          <Eye size={22} />
        </span>
        <span style={{ flex: 1 }}>
          <span className="pkg-head-title">Topic preview</span>
          <span className="muted" style={{ display: 'block', fontSize: 13 }}>
            How this topic appears to guests on the TV.
          </span>
        </span>
        {topics.length > 0 && (
          <Select value={sel?.id ?? ''} onChange={(e) => onSelect(e.target.value)} aria-label="Topic">
            {topics.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </Select>
        )}
      </div>
      <div className="info-tv">
        <div className="info-tv-side">
          {logo ? <img src={logo} alt="" className="info-tv-logo" /> : <strong style={{ fontSize: 13 }}>{site.name}</strong>}
          {visible.map((t) => (
            <div key={t.id} className="info-tv-item" style={t.id === sel?.id ? { background: accent } : undefined}>
              {t.title}
            </div>
          ))}
        </div>
        <div className="info-tv-main">
          <div className="info-tv-top">
            <strong>Hotel Information</strong>
            <span>
              {now.toLocaleDateString([], { ...opts, weekday: 'short', day: 'numeric', month: 'short' })} | {now.toLocaleTimeString([], { ...opts, hour: '2-digit', minute: '2-digit', hour12: !!b.hour12 })}
            </span>
          </div>
          <div className="info-tv-hero" style={img ? { backgroundImage: `linear-gradient(0deg, rgba(10,8,30,.85), rgba(10,8,30,.05) 65%), url(${img})` } : undefined}>
            <strong>{sel?.title ?? 'No topic yet'}</strong>
            {sel?.body && <p>{sel.body}</p>}
          </div>
        </div>
      </div>
      <div className="info-tv-stand" />

      {sel && (
        <>
          <div className="row" style={{ justifyContent: 'space-between', marginTop: 18 }}>
            <h3 style={{ margin: 0 }}>Topic details</h3>
            {onEdit && (
              <button className="btn" onClick={onEdit}>
                <Pencil size={16} /> Edit topic
              </button>
            )}
          </div>
          <div className="row" style={{ gap: 14, marginTop: 12, flexWrap: 'nowrap' }}>
            <span className="fac-thumb" style={{ width: 120, height: 72 }}>
              {img ? <img src={img} alt="" /> : <ImageOff size={18} />}
            </span>
            <span>
              <strong style={{ display: 'block', fontSize: 16 }}>{sel.title}</strong>
              {sel.subtitle && <span className="muted" style={{ display: 'block', fontSize: 13 }}>{sel.subtitle}</span>}
              <span className={`badge ${sel.enabled ? 'ok' : ''}`} style={{ marginTop: 6, display: 'inline-block' }}>
                {sel.enabled ? 'Visible on TV' : 'Hidden'}
              </span>
            </span>
          </div>
          <div className="info-facts">
            <div>
              <FileText size={17} />
              <span>
                <span className="muted">Text</span>
                {sel.body ? `${sel.body.trim().split(/\s+/).length} words` : 'None'}
              </span>
            </div>
            <div>
              <Layers size={17} />
              <span>
                <span className="muted">Media</span>
                {(sel.imageAssetId ? 1 : 0) + (sel.galleryAssetIds?.length ?? 0)} images
              </span>
            </div>
            <div>
              <Images size={17} />
              <span>
                <span className="muted">Carousel</span>
                {sel.galleryAssetIds?.length ? `${sel.galleryAssetIds.length} photos` : 'None'}
              </span>
            </div>
            <div>
              <Eye size={17} />
              <span>
                <span className="muted">Display order</span>
                {topics.indexOf(sel) + 1} of {topics.length}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────── Hotel information ───────────────────────────

export interface Topic {
  id: string;
  title: string;
  subtitle: string | null;
  icon: string;
  imageAssetId: string | null;
  body: string | null;
  galleryAssetIds?: string[];
  enabled: boolean;
}

/** Extra photos of a topic, shown on the TV as a carousel (e.g. Gallery). */
function GalleryField({ ids, onChange }: { ids: string[]; onChange: (ids: string[]) => void }) {
  const url = useAssetUrls();
  const qc = useQueryClient();
  const upload = useMutation({
    mutationFn: async (files: FileList) => {
      const added: string[] = [];
      for (const f of Array.from(files)) added.push((await api.upload<{ id: string }>('/assets', f)).id);
      return added;
    },
    onSuccess: async (added) => {
      await qc.invalidateQueries({ queryKey: ['assets'] });
      onChange([...ids, ...added]);
    },
  });
  return (
    <Field label="Carousel photos" hint="Optional. Shown one after another when the guest opens this topic.">
      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        {ids.map((id, i) => (
          <div key={id} style={{ position: 'relative' }}>
            {url(id) ? <img src={url(id)!} alt="" style={{ width: 96, height: 60, objectFit: 'cover', borderRadius: 6 }} /> : <div className="logo-placeholder" style={{ width: 96, height: 60 }} />}
            <button className="icon-btn" style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(0,0,0,.6)', color: '#fff' }} onClick={() => onChange(ids.filter((_, j) => j !== i))} aria-label="Remove">
              ×
            </button>
          </div>
        ))}
        <label className="btn small" style={{ cursor: 'pointer' }}>
          {upload.isPending ? 'Uploading…' : '+ Add photos'}
          <input type="file" accept="image/*" multiple hidden onChange={(e) => e.target.files?.length && upload.mutate(e.target.files)} />
        </label>
      </div>
      <ErrorBox error={upload.error} />
    </Field>
  );
}

const infoFields: FieldSpec[] = [
  { key: 'heroTitle', label: 'Headline', type: 'text', placeholder: 'Welcome to Serenity Hotel & Resort' },
  { key: 'heroText', label: 'Intro text', type: 'textarea' },
  { key: 'heroImageAssetId', label: 'Hero photo', type: 'image', urlKey: 'heroImageUrl' },
  { key: 'videoUrl', label: 'Hotel video (MP4 URL, optional)', type: 'url' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'email', label: 'Email', type: 'text' },
  { key: 'address', label: 'Address', type: 'text' },
  { key: 'receptionHours', label: 'Reception hours', type: 'text', placeholder: '24 / 7' },
  { key: 'website', label: 'Website', type: 'text' },
];

const topicFields: FieldSpec[] = [
  { key: 'title', label: 'Title', type: 'text', required: true, placeholder: 'About Us' },
  { key: 'subtitle', label: 'Short line', type: 'text', placeholder: 'Our story and values' },
  { key: 'icon', label: 'Icon', type: 'icon' },
  { key: 'imageAssetId', label: 'Photo', type: 'image', urlKey: 'imageUrl' },
  { key: 'body', label: 'Text shown when opened', type: 'textarea' },
  {
    key: 'galleryAssetIds',
    label: 'Carousel photos',
    type: 'custom',
    render: (v, set) => <GalleryField ids={(v.galleryAssetIds as string[]) ?? []} onChange={(ids) => set({ galleryAssetIds: ids })} />,
  },
  { key: 'enabled', label: 'Shown on TV', type: 'toggle' },
];

